import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Car, ArrowLeftRight, Lock, Loader2, AlertCircle, Check, RotateCcw, ChevronRight, PenTool, X, Delete, ArrowLeft, Camera, Home, Printer, ScanLine, Keyboard, RefreshCw } from "lucide-react";
import axios from "axios";

const API = "/api";

const NumPad = ({ value, onChange, maxLength = 6, alphaMode = true }) => {
  const handleKey = (k) => {
    if (k === "del") onChange(value.slice(0, -1));
    else if (value.length < maxLength) onChange(value + k);
  };
  const keys = alphaMode
    ? ["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", ""]
    : ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];
  return (
    <div className="grid grid-cols-3 gap-3 max-w-[320px] mx-auto">
      {keys.map((k, i) =>
        k === "" ? <div key={i} /> : (
          <button key={i} type="button" onClick={() => handleKey(k)}
            className="h-16 rounded-2xl text-2xl font-semibold bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-white transition-colors flex items-center justify-center">
            {k === "del" ? <Delete className="w-6 h-6" /> : k}
          </button>
        )
      )}
    </div>
  );
};

// Virtual QWERTY keyboard for booking code
const AlphaNumPad = ({ value, onChange, maxLength = 6 }) => {
  const handleKey = (k) => {
    if (k === "DEL") onChange(value.slice(0, -1));
    else if (k === "CLR") onChange("");
    else if (value.length < maxLength) onChange(value + k);
  };
  const rows = [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["Z", "X", "C", "V", "B", "N", "M"],
    ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ];
  return (
    <div className="space-y-2 max-w-[600px] mx-auto">
      {rows.map((row, ri) => (
        <div key={ri} className="flex justify-center gap-1.5">
          {row.map((k) => (
            <button key={k} type="button" onClick={() => handleKey(k)}
              className="h-14 w-14 rounded-xl text-lg font-semibold bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-white transition-colors flex items-center justify-center">
              {k}
            </button>
          ))}
        </div>
      ))}
      <div className="flex justify-center gap-2 pt-1">
        <button onClick={() => handleKey("CLR")} className="h-12 px-6 rounded-xl text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors">
          Cancella
        </button>
        <button onClick={() => handleKey("DEL")} className="h-12 px-6 rounded-xl text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors flex items-center gap-2">
          <Delete className="w-4 h-4" /> Elimina
        </button>
      </div>
    </div>
  );
};

// Numeric pad for contract number (e.g. 1/2026)
const ContractNumPad = ({ value, onChange, maxLength = 10 }) => {
  const handleKey = (k) => {
    if (k === "DEL") onChange(value.slice(0, -1));
    else if (k === "CLR") onChange("");
    else if (value.length < maxLength) onChange(value + k);
  };
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "/", "0", "DEL"];
  return (
    <div className="space-y-2 max-w-[320px] mx-auto">
      <div className="grid grid-cols-3 gap-3">
        {keys.map((k, i) => (
          <button key={i} type="button" onClick={() => handleKey(k)}
            className="h-16 rounded-2xl text-2xl font-semibold bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-white transition-colors flex items-center justify-center">
            {k === "DEL" ? <Delete className="w-6 h-6" /> : k}
          </button>
        ))}
      </div>
      <div className="flex justify-center pt-1">
        <button onClick={() => handleKey("CLR")} className="h-12 px-6 rounded-xl text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors">
          Cancella tutto
        </button>
      </div>
    </div>
  );
};

const DoneScreen = ({ mode, vehicle, client, rental, onHome, onPrint, autoPrint }) => {
  const [countdown, setCountdown] = useState(30);
  const [phase, setPhase] = useState(mode === "ritiro" ? "printing" : "thanks"); // printing -> thanks

  // Auto-trigger print on mount for ritiro
  useEffect(() => {
    if (mode === "ritiro" && phase === "printing") {
      const t = setTimeout(() => {
        onPrint();
        setTimeout(() => setPhase("thanks"), 4000);
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [mode, phase, onPrint]);

  // Countdown only in "thanks" phase
  useEffect(() => {
    if (phase !== "thanks") return;
    if (countdown <= 0) { onHome(); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, phase, onHome]);

  const handleManualPrint = () => {
    setPhase("printing");
    onPrint();
    setTimeout(() => { setPhase("thanks"); setCountdown(20); }, 4000);
  };

  // ── Printing phase ──
  if (phase === "printing") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center">
        <div className="text-center space-y-8">
          <div className="w-28 h-28 rounded-full bg-blue-950 flex items-center justify-center mx-auto">
            <Printer className="w-14 h-14 text-blue-400 animate-pulse" />
          </div>
          <div className="space-y-3">
            <h1 className="text-4xl font-bold text-white">Stampa Contratto in Corso</h1>
            <p className="text-xl text-slate-400">Attendere prego...</p>
          </div>
          <div className="flex justify-center">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Thanks phase ──
  return (
    <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center">
      <div className="text-center space-y-6">
        <div className="w-24 h-24 rounded-full bg-emerald-950 flex items-center justify-center mx-auto">
          <Check className="w-12 h-12 text-emerald-400" />
        </div>
        <h1 className="text-4xl font-bold text-white">
          {mode === "ritiro" ? "Grazie, Buon Viaggio!" : "Riconsegna Completata!"}
        </h1>
        <p className="text-xl text-slate-400">
          {mode === "ritiro"
            ? `${vehicle.brand} ${vehicle.model} (${vehicle.plate})`
            : `Grazie, ${client.name}! Il veicolo e' stato riconsegnato.`}
        </p>
        {mode === "ritiro" && rental.contract_number && (
          <p className="text-base text-slate-500">Contratto N. {rental.contract_number} stampato</p>
        )}
        <div className="flex gap-4 justify-center pt-4">
          {mode === "ritiro" && rental.contract_number && (
            <Button onClick={handleManualPrint} variant="outline" className="rounded-xl px-8 h-14 text-base border-slate-700 text-slate-200 hover:bg-slate-800">
              <Printer className="w-5 h-5 mr-2" /> Ristampa
            </Button>
          )}
          <Button onClick={onHome} className="rounded-xl px-8 h-14 text-base bg-slate-800 hover:bg-slate-700 text-white">
            <Home className="w-5 h-5 mr-2" /> Home
          </Button>
        </div>
        <p className="text-sm text-slate-500">Ritorno alla home tra {countdown} secondi...</p>
      </div>
    </div>
  );
};

export default function KioskClientiPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [checkingPin, setCheckingPin] = useState(true);
  const [requirePhoto, setRequirePhoto] = useState(true);

  const [screen, setScreen] = useState("home"); // home, code, summary, photo, signing, step, done
  const [mode, setMode] = useState(null); // "ritiro" | "riconsegna"
  const [codeInput, setCodeInput] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [rentalData, setRentalData] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Signature flow (for ritiro)
  const [sigStep, setSigStep] = useState(1); // 1-6
  const [signatures, setSignatures] = useState([null, null, null]);
  const [privacyPages, setPrivacyPages] = useState([]);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // PIN check
  useEffect(() => {
    const checkPin = async () => {
      try {
        const res = await axios.get(`${API}/signatures/kiosk/config`);
        setRequirePhoto(res.data?.require_photo ?? true);
        if (!res.data?.pin_required) {
          const tk = await axios.post(`${API}/signatures/kiosk/verify-pin`, { pin: "" });
          sessionStorage.setItem("kiosk_token", tk.data.kiosk_token || "");
          setAuthenticated(true);
        } else if (sessionStorage.getItem("kiosk_token")) {
          setAuthenticated(true);
        }
      } catch {
        if (sessionStorage.getItem("kiosk_token")) setAuthenticated(true);
      }
      setCheckingPin(false);
    };
    checkPin();
  }, []);

  const verifyPin = async () => {
    setPinError("");
    try {
      const res = await axios.post(`${API}/signatures/kiosk/verify-pin`, { pin: pinInput });
      if (res.data.valid) {
        sessionStorage.setItem("kiosk_token", res.data.kiosk_token || "");
        sessionStorage.setItem("kiosk_client_auth", "true");
        setAuthenticated(true);
      }
    } catch { setPinError("PIN non valido"); setPinInput(""); }
  };

  const kioskPost = async (url, body) => {
    const headers = () => ({ headers: { "X-Kiosk-Token": sessionStorage.getItem("kiosk_token") || "" } });
    try {
      return await axios.post(url, body, headers());
    } catch (err) {
      if (err.response?.status === 401) {
        const cfg = await axios.get(`${API}/signatures/kiosk/config`).catch(() => null);
        if (cfg && !cfg.data?.pin_required) {
          const tk = await axios.post(`${API}/signatures/kiosk/verify-pin`, { pin: "" });
          sessionStorage.setItem("kiosk_token", tk.data.kiosk_token || "");
          return await axios.post(url, body, headers());
        }
        sessionStorage.removeItem("kiosk_token");
        alert("Sessione kiosk scaduta: ricarica la pagina e inserisci di nuovo il PIN");
      }
      throw err;
    }
  };

  // Poll for self-service signature requests from admin panel (runs even before PIN auth)
  useEffect(() => {
    if (checkingPin) return;
    // Poll on PIN screen OR on home screen (when already authenticated)
    const canPoll = !authenticated || screen === "home";
    if (!canPoll) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/signatures/kiosk/poll?target=selfservice`);
        if (res.data.status === "pending" && res.data.data) {
          const d = res.data.data;
          setRentalData({ rental: d.rental, client: d.client, vehicle: d.vehicle });
          setMode("ritiro");
          // Auto-authenticate and skip to summary (admin already authorized this)
          if (!authenticated) {
            sessionStorage.setItem("kiosk_client_auth", "true");
            setAuthenticated(true);
          }
          setScreen("summary");
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [authenticated, screen, checkingPin]);

  // Listen for USB QR reader keystrokes (no input element = no virtual keyboard)
  useEffect(() => {
    if (screen !== "qr") return;
    let buffer = "";
    let timeout = null;
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        if (buffer.length > 0) {
          setCodeInput(buffer.toUpperCase());
          setLookupError("");
          // Auto-lookup after scan
          setTimeout(() => {
            const btn = document.querySelector('[data-auto-lookup]');
            if (btn) btn.click();
          }, 100);
        }
        buffer = "";
        return;
      }
      if (e.key === "Backspace") { buffer = buffer.slice(0, -1); return; }
      if (e.key.length === 1) {
        buffer += e.key;
        setCodeInput(buffer.toUpperCase());
        clearTimeout(timeout);
        timeout = setTimeout(() => { buffer = ""; }, 5000);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timeout);
    };
  }, [screen]);

  // Lookup
  const lookupCode = async () => {
    if (!codeInput.trim()) return;
    setLookupError("");
    setLookupLoading(true);
    try {
      const res = await axios.post(`${API}/kiosk/lookup`, {
        booking_code: codeInput.trim(),
        mode: mode,
      });
      const rental = res.data.rental;
      if (mode === "ritiro" && rental.status !== "prenotazione") {
        setLookupError(`Questo noleggio e' in stato "${rental.status}". Per il ritiro serve una prenotazione.`);
        setLookupLoading(false);
        return;
      }
      if (mode === "riconsegna" && rental.status !== "attivo") {
        setLookupError(`Questo noleggio e' in stato "${rental.status}". Per la riconsegna serve un noleggio attivo.`);
        setLookupLoading(false);
        return;
      }
      setRentalData(res.data);
      setScreen("summary");
    } catch (err) {
      setLookupError(err.response?.data?.detail || "Codice non trovato");
    }
    setLookupLoading(false);
  };

  // Signature canvas
  const sigIndex = sigStep === 2 ? 0 : sigStep === 4 ? 1 : sigStep === 6 ? 2 : -1;

  useEffect(() => {
    if ((sigStep === 2 || sigStep === 4 || sigStep === 6) && canvasRef.current && screen === "signing") {
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.strokeStyle = "#000"; ctx.lineWidth = 1.2;
      setHasDrawn(false);
    }
  }, [sigStep, screen]);

  useEffect(() => {
    if (screen === "signing" && sigStep === 1) {
      axios.get(`${API}/signatures/privacy-preview`).then(r => setPrivacyPages(r.data.pages || [])).catch(() => {});
    }
  }, [screen, sigStep]);

  const lastPosRef = useRef(null);
  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top, pressure: e.pressure || 0.5 };
  };
  const startDraw = (e) => { e.preventDefault(); setIsDrawing(true); const p = getPos(e); lastPosRef.current = p; const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => {
    if (!isDrawing) return; e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = Math.max(0.8, 1.2 * (p.pressure * 2));
    const prev = lastPosRef.current;
    if (prev) {
      const mx = (prev.x + p.x) / 2;
      const my = (prev.y + p.y) / 2;
      ctx.quadraticCurveTo(prev.x, prev.y, mx, my);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(mx, my);
    }
    lastPosRef.current = p;
    setHasDrawn(true);
  };
  const endDraw = (e) => { if (e) e.preventDefault(); setIsDrawing(false); lastPosRef.current = null; };
  const clearCanvas = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); const dpr = window.devicePixelRatio || 1; ctx.clearRect(0, 0, c.width / dpr, c.height / dpr); setHasDrawn(false); };

  const captureCanvas = () => canvasRef.current.toDataURL("image/png").split(",")[1];

  const saveAndNext = () => {
    const b64 = captureCanvas();
    const s = [...signatures]; s[sigIndex] = b64; setSignatures(s);
    setSigStep(sigStep + 1);
  };

  const confirmFinalSignature = async () => {
    const b64 = captureCanvas();
    const finalSigs = [...signatures]; finalSigs[2] = b64;
    const primarySig = finalSigs[2] || finalSigs[1] || finalSigs[0];
    setSubmitting(true);
    try {
      // Apply signature
      await kioskPost(`${API}/signatures/kiosk/complete`, {
        rental_id: rentalData.rental.id,
        signature: primarySig,
        all_signatures: finalSigs,
      });
      // Complete pickup
      await kioskPost(`${API}/kiosk/pickup`, { rental_id: rentalData.rental.id });
      setScreen("done");
    } catch { alert("Errore durante il salvataggio"); }
    setSubmitting(false);
  };

  // Riconsegna
  const confirmReturn = async () => {
    setSubmitting(true);
    try {
      await kioskPost(`${API}/kiosk/return`, { rental_id: rentalData.rental.id });
      setScreen("done");
    } catch (err) { alert(err.response?.data?.detail || "Errore riconsegna"); }
    setSubmitting(false);
  };

  const goHome = () => {
    stopCamera();
    setScreen("home"); setMode(null); setCodeInput(""); setLookupError("");
    setRentalData(null); setSigStep(1); setSignatures([null, null, null]);
    setHasDrawn(false); setSubmitting(false); setCapturedPhoto(null);
  };

  const printContract = async () => {
    if (!rentalData?.rental?.id) return;
    try {
      const res = await axios.get(`${API}/kiosk/contract/${rentalData.rental.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      // Use embedded iframe to print without popup
      let iframe = document.getElementById("kiosk-print-iframe");
      if (iframe) iframe.remove();
      iframe = document.createElement("iframe");
      iframe.id = "kiosk-print-iframe";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.border = "none";
      iframe.style.opacity = "0.01";
      document.body.appendChild(iframe);
      iframe.src = url;
      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.error("Print error:", e);
            // Last resort: navigate current page to PDF
            window.location.href = url;
          }
        }, 800);
      };
    } catch (err) {
      console.error("Errore stampa contratto:", err);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Errore webcam:", err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const b64 = canvas.toDataURL("image/jpeg", 0.8).split(",")[1];
    setCapturedPhoto(b64);
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera();
  };

  const confirmPhoto = async () => {
    if (!capturedPhoto || !rentalData) return;
    setSubmitting(true);
    try {
      await kioskPost(`${API}/kiosk/save-photo`, {
        rental_id: rentalData.rental.id,
        photo: capturedPhoto,
      });
      stopCamera();
      setScreen("signing");
      setSigStep(1);
      setSignatures([null, null, null]);
    } catch { alert("Errore salvataggio foto"); }
    setSubmitting(false);
  };

  useEffect(() => {
    if (screen === "photo" && !capturedPhoto) {
      startCamera();
    }
    return () => { if (screen !== "photo") stopCamera(); };
  }, [screen, capturedPhoto]);

  const fmtDate = (d) => { if (!d) return "-"; const [y, m, dd] = d.split("-"); return `${dd}/${m}/${y}`; };

  const rental = rentalData?.rental || {};
  const client = rentalData?.client || {};
  const vehicle = rentalData?.vehicle || {};

  // ─── PIN Screen ───
  if (!authenticated || checkingPin) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center">
        <div className="text-center space-y-6 w-full max-w-sm px-6">
          <div className="w-20 h-20 rounded-full bg-blue-950 flex items-center justify-center mx-auto">
            <Lock className="w-10 h-10 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">RG Rent Kiosk</h1>
          <p className="text-slate-400 text-base">Inserisci il PIN per accedere</p>
          {checkingPin ? <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-400" /> : (
            <div className="space-y-5">
              <div className="flex items-center justify-center gap-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${i < pinInput.length ? "bg-blue-500 border-blue-500" : "bg-transparent border-slate-600"}`} />
                ))}
              </div>
              {pinError && <div className="flex items-center justify-center gap-2 text-red-400 text-sm"><AlertCircle className="w-4 h-4" /> {pinError}</div>}
              <NumPad value={pinInput} onChange={setPinInput} maxLength={6} alphaMode={false} />
              <Button onClick={verifyPin} disabled={pinInput.length < 4} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-12 text-base">Accedi</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── HOME ───
  if (screen === "home") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center px-8">
        <div className="text-center space-y-10 max-w-xl">
          <div>
            <img src="/rg-rent-logo.png" alt="RG Rent" className="w-48 h-auto mx-auto mb-4" />
            <p className="text-2xl text-slate-400 mt-3 font-light">Benvenuto!</p>
          </div>
          <div className="flex gap-6">
            <button
              onClick={() => { setMode("ritiro"); setScreen("qr"); }}
              data-testid="kiosk-pickup-btn"
              className="flex-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-3xl p-8 transition-all shadow-lg hover:shadow-xl"
            >
              <Car className="w-16 h-16 mx-auto mb-4" />
              <span className="text-2xl font-bold block">Ritiro Veicolo</span>
            </button>
            <button
              onClick={() => { setMode("riconsegna"); setScreen("code"); }}
              data-testid="kiosk-return-btn"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-3xl p-8 transition-all shadow-lg hover:shadow-xl"
            >
              <ArrowLeftRight className="w-16 h-16 mx-auto mb-4" />
              <span className="text-2xl font-bold block">Riconsegna Veicolo</span>
            </button>
          </div>
        </div>
        <button
          onClick={() => {
            if (window.confirm("Svuotare la cache del browser? La pagina si ricarichera'.")) {
              caches.keys().then(names => names.forEach(name => caches.delete(name)));
              window.location.reload(true);
            }
          }}
          className="fixed bottom-6 left-6 flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white text-sm transition-colors"
          data-testid="kiosk-reset-cache"
        >
          <RefreshCw className="w-4 h-4" /> Reset Cache
        </button>
      </div>
    );
  }

  // ─── QR SCAN SCREEN ───
  if (screen === "qr") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center px-8">
        <div className="text-center space-y-8 max-w-lg w-full">
          <div className="w-28 h-28 rounded-full bg-blue-950 flex items-center justify-center mx-auto">
            <ScanLine className="w-14 h-14 text-blue-400 animate-pulse" />
          </div>
          <div className="space-y-3">
            <h2 className="text-3xl font-bold text-white">Scansiona QR Code</h2>
            <p className="text-lg text-slate-400">Avvicina il QR Code della prenotazione al lettore</p>
          </div>

          {codeInput && (
            <div className="flex justify-center">
              <div className="px-6 py-3 rounded-xl border-2 border-blue-500 bg-blue-950 min-w-[200px]">
                <span className="text-2xl font-bold font-mono text-blue-400 tracking-wider">{codeInput}</span>
              </div>
            </div>
          )}

          {lookupError && (
            <div className="flex items-center justify-center gap-2 text-red-400 text-sm bg-red-950 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 shrink-0" /> {lookupError}
            </div>
          )}

          {lookupLoading && (
            <div className="flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          )}

          <div className="flex gap-4 justify-center pt-4">
            <Button variant="outline" onClick={goHome} className="rounded-xl px-8 h-14 text-base border-slate-700 text-slate-200 hover:bg-slate-800">
              <ArrowLeft className="w-5 h-5 mr-2" /> Indietro
            </Button>
            <Button onClick={() => { setCodeInput(""); setScreen("code"); }} className="rounded-xl px-8 h-14 text-base bg-slate-700 hover:bg-slate-600 text-white">
              <Keyboard className="w-5 h-5 mr-2" /> Inserisci Manualmente
            </Button>
          </div>

          <div className="flex justify-center gap-1.5 pt-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: "200ms" }} />
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: "400ms" }} />
          </div>
          <p className="text-sm text-slate-600">In attesa di scansione...</p>
          <button data-auto-lookup className="hidden" onClick={lookupCode} />
        </div>
      </div>
    );
  }

  // ─── CODE INPUT ───
  if (screen === "code") {
    const isReturn = mode === "riconsegna";
    const maxLen = isReturn ? 10 : 6;
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center px-8">
        <div className="text-center space-y-6 max-w-xl w-full">
          <h2 className="text-3xl font-bold text-white">
            {isReturn ? "Riconsegna Veicolo" : "Ritiro Veicolo"}
          </h2>
          <p className="text-lg text-slate-400">
            {isReturn ? "Inserisci il numero contratto" : "Inserisci il codice prenotazione"}
          </p>

          {/* Code display */}
          {isReturn ? (
            <div className="flex justify-center">
              <div className="px-6 py-4 rounded-xl border-2 border-emerald-600 bg-emerald-950 min-w-[200px] text-center">
                <span className="text-4xl font-bold font-mono text-emerald-400 tracking-wider">{codeInput || <span className="text-emerald-700">_/_</span>}</span>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`w-14 h-16 rounded-xl border-2 flex items-center justify-center text-3xl font-bold transition-all ${
                  i < codeInput.length ? "border-blue-500 bg-blue-950 text-blue-400" : "border-slate-700 text-slate-600"
                }`}>
                  {codeInput[i] || ""}
                </div>
              ))}
            </div>
          )}

          {lookupError && (
            <div className="flex items-center justify-center gap-2 text-red-400 text-sm bg-red-950 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 shrink-0" /> {lookupError}
            </div>
          )}

          {isReturn ? (
            <ContractNumPad value={codeInput} onChange={(v) => { setCodeInput(v); setLookupError(""); }} maxLength={maxLen} />
          ) : (
            <AlphaNumPad value={codeInput} onChange={(v) => { setCodeInput(v.toUpperCase()); setLookupError(""); }} maxLength={maxLen} />
          )}

          <div className="flex gap-4 pt-2 justify-center">
            <Button variant="outline" onClick={goHome} className="rounded-xl px-8 h-14 text-base border-slate-700 text-slate-200 hover:bg-slate-800">
              <ArrowLeft className="w-5 h-5 mr-2" /> Indietro
            </Button>
            <Button onClick={lookupCode} disabled={codeInput.length < 1 || lookupLoading}
              className={`rounded-xl px-8 h-14 text-base text-white ${isReturn ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}>
              {lookupLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Cerca <ChevronRight className="w-5 h-5 ml-2" /></>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── SUMMARY ───
  if (screen === "summary") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center px-8">
        <div className="max-w-lg w-full space-y-6">
          <h2 className="text-3xl font-bold text-white text-center">
            {mode === "ritiro" ? "Riepilogo Ritiro" : "Riepilogo Riconsegna"}
          </h2>
          <div className="bg-slate-900 rounded-2xl p-6 space-y-4 border border-slate-800">
            <div className="text-center pb-3 border-b border-slate-800">
              <p className="text-2xl font-bold text-white">{vehicle.brand} {vehicle.model}</p>
              <p className="text-lg text-slate-400 font-mono">{vehicle.plate}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-base">
              <div><span className="text-slate-500">Cliente:</span><br/><span className="font-semibold text-white">{client.name} {client.surname}</span></div>
              <div><span className="text-slate-500">Codice:</span><br/><span className="font-semibold text-white font-mono">{rental.booking_code}</span></div>
              <div><span className="text-slate-500">Ritiro:</span><br/><span className="font-semibold text-white">{fmtDate(rental.start_date)} {rental.start_time || ""}</span></div>
              <div><span className="text-slate-500">Riconsegna:</span><br/><span className="font-semibold text-white">{fmtDate(rental.end_date)} {rental.end_time || ""}</span></div>
              <div><span className="text-slate-500">Totale:</span><br/><span className="font-bold text-white text-xl">{rental.total_amount?.toFixed(2)} EUR</span></div>
              <div><span className="text-slate-500">Acconto:</span><br/><span className="font-semibold text-white">{(rental.deposit_amount || 0).toFixed(2)} EUR</span></div>
            </div>
          </div>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => { setScreen("code"); setRentalData(null); }} className="rounded-xl px-8 h-14 text-base border-slate-700 text-slate-200 hover:bg-slate-800">
              <ArrowLeft className="w-5 h-5 mr-2" /> Indietro
            </Button>
            {mode === "ritiro" ? (
              rental.signed ? (
                <Button onClick={async () => {
                  setSubmitting(true);
                  try {
                    await kioskPost(`${API}/kiosk/pickup`, { rental_id: rental.id });
                    setScreen("done");
                  } catch (err) { alert(err.response?.data?.detail || "Errore"); }
                  setSubmitting(false);
                }}
                  disabled={submitting}
                  className="rounded-xl px-8 h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Printer className="w-5 h-5 mr-2" /> Ritira e Stampa</>}
                </Button>
              ) : (
              <Button onClick={() => {
                if (requirePhoto) {
                  setScreen("photo"); setCapturedPhoto(null);
                } else {
                  setScreen("signing"); setSigStep(1); setSignatures([null, null, null]);
                }
              }}
                className="rounded-xl px-8 h-14 text-base bg-blue-600 hover:bg-blue-700 text-white">
                {requirePhoto ? <Camera className="w-5 h-5 mr-2" /> : <PenTool className="w-5 h-5 mr-2" />} Procedi
              </Button>
              )
            ) : (
              <Button onClick={confirmReturn} disabled={submitting}
                className="rounded-xl px-8 h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-2" /> Conferma Riconsegna</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── PHOTO ───
  if (screen === "photo") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center px-8">
        <div className="max-w-lg w-full space-y-6 text-center">
          <h2 className="text-3xl font-bold text-white">Foto Identificativa</h2>
          <p className="text-lg text-slate-400">Posizionati davanti alla webcam</p>

          <div className="relative rounded-2xl overflow-hidden border-2 border-slate-700 bg-black mx-auto" style={{ width: 480, height: 360 }}>
            {!capturedPhoto ? (
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            ) : (
              <img src={`data:image/jpeg;base64,${capturedPhoto}`} alt="Foto" className="w-full h-full object-cover" />
            )}
          </div>

          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={goHome} className="rounded-xl px-6 h-14 text-base text-red-400 border-red-800 hover:bg-red-950">
              <X className="w-5 h-5 mr-2" /> Annulla
            </Button>
            {!capturedPhoto ? (
              <Button onClick={takePhoto} className="rounded-xl px-8 h-14 text-base bg-blue-600 hover:bg-blue-700 text-white">
                <Camera className="w-5 h-5 mr-2" /> Scatta Foto
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={retakePhoto} className="rounded-xl px-6 h-14 text-base border-slate-700 text-slate-200 hover:bg-slate-800">
                  <RotateCcw className="w-5 h-5 mr-2" /> Riscatta
                </Button>
                <Button onClick={confirmPhoto} disabled={submitting} className="rounded-xl px-8 h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-2" /> Conferma e Firma</>}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── SIGNING (6-step, same as FirmaKioskPage) ───
  if (screen === "signing") {
    const renderCanvas = (label) => (
      <div className="flex flex-col h-full">
        <h2 className="text-xl font-bold text-white text-center shrink-0">{label}</h2>
        <p className="text-center text-sm text-slate-400 shrink-0">{client.name} {client.surname} — Firma nel riquadro</p>
        <div className="relative border-2 border-slate-600 rounded-xl bg-white overflow-hidden min-h-0 mt-2" style={{ touchAction: "none", maxHeight: "200px", flex: "1 1 200px" }}>
          <canvas ref={canvasRef} className="w-full h-full" style={{ cursor: "crosshair" }}
            onPointerDown={startDraw} onPointerMove={draw} onPointerUp={endDraw} onPointerLeave={endDraw} />
          {!hasDrawn && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><p className="text-slate-300 text-2xl font-medium">Firma qui</p></div>}
        </div>
      </div>
    );

    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col">
        <div className="h-2 bg-slate-800 w-full"><div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${(sigStep / 6) * 100}%` }} /></div>
        <div className="px-6 py-3 border-b border-slate-800 flex items-center justify-between bg-slate-900">
          <div className="flex items-center gap-3">
            <PenTool className="w-5 h-5 text-blue-400" />
            <p className="text-sm font-bold text-white">{client.name} {client.surname} — {vehicle.brand} {vehicle.model} ({vehicle.plate})</p>
          </div>
          <span className="text-sm text-slate-400">Passaggio {sigStep}/6</span>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 max-w-3xl mx-auto w-full">
          {sigStep === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white text-center">Autorizzazione Privacy</h2>
              {privacyPages.length > 0 ? privacyPages.map((p, i) => (
                <img key={i} src={`data:image/png;base64,${p}`} alt={`Privacy ${i + 1}`} className="w-full rounded-xl border border-slate-700 shadow-sm" draggable={false} />
              )) : <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />}
            </div>
          )}
          {sigStep === 2 && renderCanvas("Firma Accettazione Privacy")}
          {sigStep === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white text-center">Verifica Dati</h2>
              <div className="p-5 bg-slate-900 rounded-xl border border-slate-800 grid grid-cols-2 gap-4 text-base">
                <div><span className="text-slate-500">Nome:</span><br/><span className="font-semibold text-white">{client.name} {client.surname}</span></div>
                <div><span className="text-slate-500">Cod. Fiscale:</span><br/><span className="font-semibold text-white">{client.fiscal_code || "-"}</span></div>
                <div><span className="text-slate-500">Telefono:</span><br/><span className="font-semibold text-white">{client.phone || "-"}</span></div>
                <div><span className="text-slate-500">Email:</span><br/><span className="font-semibold text-white">{client.email || "-"}</span></div>
                <div><span className="text-slate-500">Veicolo:</span><br/><span className="font-semibold text-white">{vehicle.brand} {vehicle.model} ({vehicle.plate})</span></div>
                <div><span className="text-slate-500">Periodo:</span><br/><span className="font-semibold text-white">{fmtDate(rental.start_date)} → {fmtDate(rental.end_date)}</span></div>
              </div>
            </div>
          )}
          {sigStep === 4 && renderCanvas("Firma Conferma Dati")}
          {sigStep === 5 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white text-center">Termini e Condizioni</h2>
              <div className="p-5 bg-slate-900 rounded-xl border border-slate-800 text-sm text-slate-300 leading-relaxed space-y-3">
                <p><b className="text-white">1.</b> Il presente contratto e' regolato dalle condizioni qui riportate. Il veicolo viene consegnato in buono stato.</p>
                <p><b className="text-white">2.</b> Il contratto si perfeziona con la consegna del veicolo e la sottoscrizione del presente modulo.</p>
                <p><b className="text-white">3.</b> Il Locatario si impegna a utilizzare il veicolo con diligenza e rispettare il Codice della Strada.</p>
                <p><b className="text-white">4.</b> Il veicolo e' coperto da assicurazione RCA. Danni non coperti sono a carico del Locatario.</p>
                <p><b className="text-white">5.</b> Il veicolo deve essere restituito nella data concordata. Ritardi comportano addebiti aggiuntivi.</p>
                <p className="font-semibold text-white pt-2">Proseguendo con la firma, dichiaro di aver letto e accettato le condizioni.</p>
              </div>
            </div>
          )}
          {sigStep === 6 && renderCanvas("Firma Contratto")}
        </div>
        <div className="px-6 py-3 border-t border-slate-800 bg-slate-900 flex justify-between items-center shrink-0">
          <Button variant="outline" onClick={goHome} className="rounded-xl px-5 h-11 text-sm text-red-400 border-red-800 hover:bg-red-950">
            <X className="w-4 h-4 mr-2" /> Annulla
          </Button>
          <div>
            {(sigStep === 2 || sigStep === 4 || sigStep === 6) && (
              <Button variant="outline" onClick={clearCanvas} className="rounded-xl px-5 h-11 text-sm text-slate-300 border-slate-700 mr-3 hover:bg-slate-800">
                <RotateCcw className="w-4 h-4 mr-2" /> Pulisci
              </Button>
            )}
          </div>
          <div>
            {(sigStep === 1 || sigStep === 3) && (
              <Button onClick={() => setSigStep(sigStep + 1)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-11 text-sm">
                Avanti <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {sigStep === 5 && (
              <Button onClick={() => setSigStep(6)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-11 text-sm">
                <PenTool className="w-4 h-4 mr-2" /> Firma
              </Button>
            )}
            {(sigStep === 2 || sigStep === 4) && (
              <Button onClick={saveAndNext} disabled={!hasDrawn} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-8 h-11 text-sm">
                <Check className="w-4 h-4 mr-2" /> Conferma e Avanti
              </Button>
            )}
            {sigStep === 6 && (
              <Button onClick={confirmFinalSignature} disabled={!hasDrawn || submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-8 h-11 text-sm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Conferma Firma
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── DONE ───
  if (screen === "done") {
    return <DoneScreen mode={mode} vehicle={vehicle} client={client} rental={rental} onHome={goHome} onPrint={printContract} />;
  }

  return null;
}
